"use client";

import { forwardRef, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { SectionBlock } from "@/components/blocks/SectionBlock";
import { DetailedContactForm } from "@/components/blocks/DetailedContactForm";
import { useScrollReveal, type RevealState } from "@/shared/lib/useScrollReveal";
import type { Specialist } from "@/entities/specialist";
import type { Approach } from "@/entities/approach";

/* ─────────────────────────────────────────────────────────────
   RevealRow — вылет с одной стороны (num + text синхронно)
   isCentered управляется родителем через scroll-расчёт,
   гарантирует ровно ОДИН окрашенный блок в любой момент.
───────────────────────────────────────────────────────────── */
type RowPhase = "revealed" | "hidden" | "animating";

function RevealRow({
  p,
  i,
  isCentered,
  rowRefCallback,
}: {
  p: Approach;
  i: number;
  isCentered: boolean;
  rowRefCallback: (el: HTMLDivElement | null) => void;
}) {
  const isOdd = i % 2 === 0; // row 0,2 = from right; row 1,3 = from left
  const [phase, setPhase] = useState<RowPhase>("revealed");
  const animatedRef = useRef(false);

  // Mount → hide (SSR safe)
  useEffect(() => {
    setPhase("hidden");
  }, []);

  // One-shot animation IO (threshold 0.4 = row clearly entering view)
  const rowRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (phase !== "hidden") return;
    const el = rowRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !animatedRef.current) {
          animatedRef.current = true;
          setPhase("animating");
          io.disconnect();
        }
      },
      { threshold: 0.4 },
    );

    io.observe(el);
    return () => io.disconnect();
  }, [phase]);

  const hidden: CSSProperties = {
    opacity: 0,
    transform: isOdd ? "translateX(120%)" : "translateX(-120%)",
  };
  const kf = isOdd ? "slide-from-right-far" : "slide-from-left-far";

  const numStyle: CSSProperties =
    phase === "revealed" ? {} :
    phase === "hidden"   ? hidden :
    { opacity: 0, animation: `${kf} 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0s forwards` };

  const txtStyle: CSSProperties =
    phase === "revealed" ? {} :
    phase === "hidden"   ? hidden :
    { opacity: 0, animation: `${kf} 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.05s forwards` };

  return (
    <div
      ref={(el) => {
        rowRef.current = el;
        rowRefCallback(el);
      }}
      className={`grid grid-cols-1 md:grid-cols-3 gap-fluid-md rounded-[var(--fluid-radius-lg)] px-fluid-md py-fluid-lg -mx-fluid-md cursor-default transition-colors duration-300 ${
        isCentered ? "bg-help" : ""
      }`}
    >
      {/* Цифра */}
      <div
        style={numStyle}
        className={`md:col-span-1 order-1 min-h-[clamp(80px,12svh,160px)] md:min-h-0 flex items-center justify-center ${
          isOdd ? "md:order-1" : "md:order-2"
        }`}
      >
        <span
          className={`font-heading text-[clamp(6rem,15vw,12rem)] leading-none select-none transition-colors duration-300 ${
            isCentered ? "text-white/25" : "text-help/15"
          }`}
        >
          {String(i + 1).padStart(2, "0")}
        </span>
      </div>

      {/* Текст */}
      <div
        style={txtStyle}
        className={`col-span-1 md:col-span-2 h-full order-2 flex flex-col justify-center ${
          isOdd ? "md:order-2" : "md:order-1"
        }`}
      >
        <h3
          className={`font-heading text-fluid-xl leading-tight mb-fluid-sm transition-colors duration-300 ${
            isCentered ? "text-white" : "text-foreground"
          }`}
        >
          {p.title}
        </h3>
        <p
          className={`font-content text-fluid-base leading-relaxed transition-colors duration-300 ${
            isCentered ? "text-white/80" : "text-foreground/80"
          }`}
        >
          {p.text}
        </p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Стиль-хелперы заголовка и формы
───────────────────────────────────────────────────────────── */
function headingBlock(state: RevealState): CSSProperties {
  if (state === "done") return {};
  if (state === "idle") return { opacity: 0, transform: "translateY(-2.5rem)" };
  return {
    opacity: 0,
    animation: "slide-from-top 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0s forwards",
  };
}

function fromBottom(state: RevealState, delay: number): CSSProperties {
  if (state === "done") return {};
  if (state === "idle") return { opacity: 0, transform: "translateY(2.5rem)" };
  return {
    opacity: 0,
    animation: `slide-from-bottom 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay.toFixed(2)}s forwards`,
  };
}

/* ─────────────────────────────────────────────────────────────
   Компонент
───────────────────────────────────────────────────────────── */
interface PrinciplesSectionProps {
  approaches: Approach[];
  specialists?: Specialist[];
}

export const PrinciplesSection = forwardRef<HTMLElement, PrinciplesSectionProps>(({ approaches, specialists = [] }, ref) => {
  const { ref: revealRef, state } = useScrollReveal("principles", 0.1);

  // Scroll-based center tracking: ровно один блок ближайший к центру viewport
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [centerIdx, setCenterIdx] = useState(-1);

  useEffect(() => {
    const calc = () => {
      const vcenter = window.innerHeight / 2;
      let best = -1;
      let bestDist = Infinity;

      rowRefs.current.forEach((el, i) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        // Блок должен быть хотя бы частично виден
        if (r.bottom < 0 || r.top > window.innerHeight) return;
        const dist = Math.abs((r.top + r.bottom) / 2 - vcenter);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });

      setCenterIdx(best);
    };

    window.addEventListener("scroll", calc, { passive: true });
    calc();
    return () => window.removeEventListener("scroll", calc);
  }, []);

  const setRefs = (el: HTMLElement | null) => {
    revealRef(el);
    if (typeof ref === "function") ref(el);
    else if (ref)
      (ref as React.MutableRefObject<HTMLElement | null>).current = el;
  };

  return (
    <section
      ref={setRefs}
      className="relative w-full h-auto flex flex-col gap-fluid-md overflow-hidden"
    >
      <SectionBlock className="flex flex-col gap-fluid-2xl p-fluid-xl">

        {/* Заголовок: одним блоком сверху вниз */}
        <div className="overflow-hidden">
          <div style={headingBlock(state)} className="text-left">
            <h2 className="font-content font-semibold text-fluid-4xl leading-tight text-foreground block select-none">
              Подходы, которые <span className="text-help">мы используем</span>
            </h2>
            <p className="font-content font-light text-fluid-xl leading-relaxed text-foreground/80 block select-none mt-fluid-sm max-w-[65ch]">
              Мы используем только проверенные, научно-обоснованные методы
              психотерапии, чтобы обеспечить наилучший результат.
            </p>
          </div>
        </div>

        {/* Подходы — scroll-based center tracking, gap-fluid-2xl */}
        <div className="flex flex-col gap-fluid-2xl pt-fluid-lg border-t border-[var(--help)]/20">
          {approaches.map((p, i) => (
            <RevealRow
              key={p.id}
              p={p}
              i={i}
              isCentered={centerIdx === i}
              rowRefCallback={(el) => { rowRefs.current[i] = el; }}
            />
          ))}
        </div>
      </SectionBlock>

      {/* Блок записи */}
      <div
        style={fromBottom(state, 0.3)}
        id="book-consultation"
        className="rounded-[var(--fluid-radius-lg)] bg-help p-fluid-xl flex flex-col gap-fluid-lg"
      >
        <div className="flex flex-col gap-fluid-xs">
          <h3 className="font-heading text-fluid-2xl text-white leading-tight">
            Записаться на консультацию
          </h3>
          <p className="font-content text-fluid-base text-white/75 leading-snug max-w-[60ch]">
            Заполните форму, и мы подберем специалиста, который работает в
            наиболее подходящем для вас подходе.
          </p>
        </div>
        <div className="pt-fluid-sm border-t border-white/20">
          <DetailedContactForm mode="inverted" specialists={specialists} />
        </div>
      </div>
    </section>
  );
});

PrinciplesSection.displayName = "PrinciplesSection";
